import React, { useState } from 'react';
import { Button, Input } from 'reactstrap';
import { makeStyles } from '@material-ui/core/styles';
import { api } from 'services/api';
import { describeApiError } from 'services/apiErrorMessage';
import NoteItem from './NoteItem';

const useStyles = makeStyles(() => ({
    tableRow: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: 'flex-end',
        padding: '10px 0',
        borderBottom: '1px solid #e9ecef',
        flexWrap: 'wrap',
    },
    tableCell: {
        flexGrow: 1,
        flexBasis: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '4px 8px',
    },
    tableTag: {
        fontSize: '0.75rem',
        color: '#525f7f',
        fontWeight: '700',
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
    },
    tableValue: {
        color: '#172430',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        fontWeight: '500',
        fontSize: '0.875rem',
        backgroundColor: '#fff',
        padding: '6px 10px',
        borderRadius: '4px',
        minHeight: '34px',
        border: '1px solid #dee2e6',
        boxShadow: '0 1px 2px rgba(50,50,93,.06)',
    },
    notesContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 0 8px',
        marginTop: '8px',
        borderTop: '1px solid #e9ecef',
    },
    noNotesMessage: {
        textAlign: 'center',
        fontSize: '1rem',
        color: '#888',
        width: '100%',
    },
    noteInputContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '10px',
        borderTop: '1px solid #ddd',
        paddingTop: '10px',
        width: '100%',
    },
    noteInput: { flexGrow: 1, marginRight: '10px' },
    noteSubmitButton: { flexShrink: 0 },
}));

const TabNegociacao = ({ order, reload, endpoint, userIsAdmin, showToast }) => {
    const classes = useStyles();
    const [comment, setComment] = useState('');
    const [loadingValues, setLoadingValues] = useState(false);
    const [precatoryValues, setPrecatoryValues] = useState({
        percentage: order.percentage,
        negotiated_value: order.negotiated_value || 0,
        readjusted_value: order.readjusted_value || '',
        calculated_date: order.calculated_date || '',
        honorary: order.honorary,
        honorary_value: order.honorary_value,
    });

    const createNote = async () => {
        if (!comment.trim()) {
            showToast('warning', 'Insira um comentário para criar uma nota!');
            return;
        }
        try {
            await api.post('/process/notes/', {
                process_id: order.id,
                comment,
                user_id: localStorage.getItem('AUTHOR_ID'),
            });
            showToast('success', 'Nota criada com sucesso!');
            setComment('');
            localStorage.removeItem('unsavedComment');
            await reload(endpoint, { loader: false });
        } catch (error) {
            showToast('error', 'Erro ao criar a nota');
            console.error('Erro ao criar a nota:', error);
        }
    };

    const getUpdatedValue = async () => {
        setLoadingValues(true);
        setPrecatoryValues((prev) => ({ ...prev, readjusted_value: "Calculando..." }));
        try {
            const response = await api.get(`/process/calc?precatory=${order.id}`);
            const data = response.data;
            setLoadingValues(false);
            setPrecatoryValues((prev) => ({
                ...prev,
                readjusted_value: data.valor_corrigido,
                calculated_date: data.data_final,
            }));
        } catch (error) {
            // Todo erro precisa sair de "Calculando..." e avisar o usuário. Antes
            // só status 500 com data.message era tratado, e o backend responde
            // {"error": ...} (incidente de 28/09/2026, precatorios_web#9).
            setLoadingValues(false);
            const bcbFora = error.response?.status === 503;
            setPrecatoryValues((prev) => ({
                ...prev,
                readjusted_value: bcbFora ? "BCB indisponível" : "Falha ao atualizar",
            }));
            showToast('error', describeApiError(error));
        }
    };

    const handleHonoraryChange = (e) => {
        const value = e.target.value;
        const updatedValue = precatoryValues.readjusted_value || 0;
        const calculatedValue = updatedValue - (updatedValue * (value / 100));
        setPrecatoryValues((prev) => ({ ...prev, [e.target.name]: value, honorary_value: calculatedValue }));
    };

    const handlePercentageChange = (e) => {
        const value = e.target.value;
        const maxLimit = userIsAdmin() ? 95 : 70;
        const valueToSave = value <= maxLimit ? value : maxLimit;
        const updatedValue = precatoryValues.honorary_value || 0;
        const calculatedValue = (updatedValue * (valueToSave / 100)).toFixed(2);
        setPrecatoryValues((prev) => ({ ...prev, [e.target.name]: valueToSave, negotiated_value: calculatedValue }));
    };

    const handleCalculatedDateChange = (e) => {
        setPrecatoryValues((prev) => ({ ...prev, calculated_date: e.target.value }));
    };

    const handleReadjustedValueChange = (e) => {
        const value = e.target.value.replace(/[^\d,.-]/g, '').replace(',', '.');
        setPrecatoryValues((prev) => ({ ...prev, readjusted_value: value }));
    };

    const saveNegotiatedValue = async () => {
        try {
            await api.patch(`/process/${order.id}/`, {
                ...precatoryValues,
                negotiated_value: parseFloat(precatoryValues.negotiated_value),
                readjusted_value: parseFloat(precatoryValues.readjusted_value),
            });
            await reload(endpoint, { loader: false });
            showToast('success', 'Valores salvos com sucesso!');
        } catch (error) {
            console.error(error);
            showToast('error', 'Falha ao salvar os valores');
        }
    };

    return (
        <div>
            <div className={classes.tableRow}>
                <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Requerente</div>
                        <div className={classes.tableValue}>{order.titular}</div>
                    </div>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Advogado</div>
                        <div className={classes.tableValue}>{order.lawyer}</div>
                    </div>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Documento</div>
                        <div className={classes.tableValue}>{order.titular_document}</div>
                    </div>
                </div>
            </div>

            <div className='column-list-duelist-modal'>
                <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Dt. liquidação</div>
                        <div className={classes.tableValue}>{order.account_settlement_date}</div>
                    </div>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Vlr. inicial</div>
                        <div className={classes.tableValue}>
                            {order.value.toLocaleString("currency", { currency: "BRL", style: "currency" })}
                        </div>
                    </div>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Dt. Expedição</div>
                        <div className={classes.tableValue}>{order.trf_protocol_date?.split(" ")[0]}</div>
                    </div>
                    <div className={classes.tableCell}>
                        <div className={classes.tableTag}>Vlr. Insc. na Expedição</div>
                        <div className={classes.tableValue}>
                            {order.proposal_value?.toLocaleString("currency", { currency: "BRL", style: "currency" })}
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.tableRow} style={{ alignItems: 'flex-end' }}>
                <div className={classes.tableCell}>
                    <div className={classes.tableTag} style={{ color: '#C2641A' }}>Mês IPCA-E</div>
                    <Input type="text" value={precatoryValues.calculated_date} onChange={handleCalculatedDateChange} bsSize="sm" />
                </div>
                <div className={classes.tableCell}>
                    <div className={classes.tableTag} style={{ color: '#C2641A' }}>Vlr. Atualizado</div>
                    <Input
                        type="text"
                        value={
                            typeof precatoryValues.readjusted_value === 'number'
                                ? precatoryValues.readjusted_value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                                : precatoryValues.readjusted_value
                        }
                        onChange={handleReadjustedValueChange}
                        onFocus={(e) => e.target.select()}
                        onBlur={(e) => {
                            const value = parseFloat(e.target.value.replace(/[^\d.-]/g, '').replace(',', '.')) || 0;
                            setPrecatoryValues((prev) => ({ ...prev, readjusted_value: value }));
                        }}
                        bsSize="sm"
                    />
                </div>
                <div className={classes.tableCell} style={{ maxWidth: '9%' }}>
                    <div className={classes.tableTag} style={{ color: '#C2641A' }}>% Honorário</div>
                    <Input type="number" name="percentage" value={precatoryValues.percentage} onChange={handleHonoraryChange} disabled={loadingValues} bsSize="sm" />
                </div>
                <div className={classes.tableCell}>
                    <div className={classes.tableTag} style={{ color: '#C2641A' }}>Vlr. Sem Honorário</div>
                    <Input type="text" readOnly bsSize="sm" value={parseFloat(precatoryValues.honorary_value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} />
                </div>
                <div className={classes.tableCell} style={{ maxWidth: '9%' }}>
                    <div className={classes.tableTag} style={{ color: '#C2641A' }}>% Negociação</div>
                    <Input type="number" name="honorary" value={precatoryValues.honorary} onChange={handlePercentageChange} disabled={loadingValues} bsSize="sm" />
                </div>
                <div className={classes.tableCell}>
                    <div className={classes.tableTag} style={{ color: '#C2641A' }}>Vlr. Negociado</div>
                    <Input type="text" readOnly bsSize="sm" value={parseFloat(precatoryValues.negotiated_value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} />
                </div>
                <div style={{ display: "flex", flexDirection: "row", gap: "4px", paddingLeft: "8px", paddingBottom: "1px" }}>
                    <Button color="primary" size="sm" onClick={getUpdatedValue}>Atualizar</Button>
                    <Button size="sm" onClick={saveNegotiatedValue} style={{ backgroundColor: '#C2641A', borderColor: '#C2641A', color: '#fff' }}>Salvar</Button>
                </div>
            </div>

            <div className={classes.notesContainer} style={{ flexDirection: 'column', flexWrap: 'wrap' }}>
                {order.comments && order.comments.length > 0 ? (
                    order.comments.map((note, index) => (
                        <NoteItem
                            key={`${note.id}-${index}`}
                            note={note}
                            reload={reload}
                            endpoint={endpoint}
                            order={order}
                            userIsAdmin={userIsAdmin}
                            showToast={showToast}
                        />
                    ))
                ) : (
                    <div className={classes.noNotesMessage}>
                        Sem notas registradas para essa ordem.
                    </div>
                )}
            </div>

            <Input
                type="textarea"
                name="comment"
                onChange={(e) => setComment(e.target.value)}
                value={comment}
                className={classes.noteInput}
                style={{ display: 'block', width: '80%', height: '80px', margin: '20px auto 0', border: '1px solid #dee2e6', borderRadius: '4px', padding: '12px', lineHeight: '1.5', fontSize: '0.9rem', backgroundColor: '#fff', resize: 'none' }}
            />
            <Button
                size="sm"
                onClick={createNote}
                className={classes.noteSubmitButton}
                style={{ display: 'block', width: '15%', padding: '10px 0', borderRadius: '4px', backgroundColor: '#C2641A', borderColor: '#C2641A', color: '#fff', cursor: 'pointer', transition: 'background-color 0.3s ease', marginLeft: "auto", marginRight: "auto", marginTop: '12px', fontSize: "0.9em" }}
            >
                Salvar Nota
            </Button>
        </div>
    );
};

export default TabNegociacao;
